'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import CinemaRoomSliderItem from '@/app/[locale]/(default-layout)/info/_components/CinemaRoomSliderItem';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';

import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

import CinemaRoomDetailModal from '@cineamo/composables/src/cinema-room-detail-modal/CinemaRoomDetailModal';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useRouter } from '@/i18n/routing';
import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { usePageName } from '@/utils/tracking-helper';

type CinemaRoomsSliderProps = {
    selectedCinemaRoomId?: string;
};

export default function CinemaRoomSlider(props: CinemaRoomsSliderProps): React.JSX.Element {
    const { selectedCinemaRoomId } = props;

    const t = useTranslations();
    const { track } = useAnalytics();
    const router = useRouter();
    const pageName = usePageName();
    const { getCinemaRoomById, cinemaRooms } = useCinemaRooms();

    const selectedCinemaRoom = selectedCinemaRoomId ? getCinemaRoomById(Number(selectedCinemaRoomId)) : null;

    const totalItems = cinemaRooms.length;

    function selectCinemaRoom(cinemaRoom: CinemaRoomDto | null) {
        const searchParams = new URLSearchParams();
        if (cinemaRoom) {
            searchParams.set('roomId', cinemaRoom.id.toString());
        }
        router.push(
            {
                pathname: '/info',
                query: Object.fromEntries(searchParams.entries())
            },
            {
                scroll: false
            }
        );
    }

    const cinemaRoomDetailModalIsOpen = !!selectedCinemaRoom;

    return (
        <>
            {cinemaRoomDetailModalIsOpen && (
                <CinemaRoomDetailModal
                    modalIsOpen={cinemaRoomDetailModalIsOpen}
                    setModalIsOpen={(state) => selectCinemaRoom(state ? selectedCinemaRoom : null)}
                    cinemaRoom={selectedCinemaRoom}
                />
            )}

            <div
                className="flex flex-col space-y-12"
                id="rooms">
                <h3 className="text-body-bold-16">
                    <DotSeparatedLabel
                        parentElement="span"
                        labels={[
                            t('info.cinema-rooms'),
                            <span
                                key={1}
                                className="text-body-bold-14 text-[var(--on-background-color)] opacity-80">
                                {totalItems}
                            </span>
                        ]}
                    />
                </h3>
                <div className="w-full mb-24">
                    <div className="flex lg:grid grid-cols-3 gap-x-16 lg:gap-x-24 lg:gap-y-32 overflow-auto lg:overflow-hidden">
                        {cinemaRooms.map((cinemaRoom) => {
                            return (
                                <div
                                    key={`CinemaRoomSliderItem#${cinemaRoom.id}`}
                                    className="w-full">
                                    <CinemaRoomSliderItem
                                        cinemaRoom={cinemaRoom}
                                        onClick={(cinemaRoom) => {
                                            track('Cinema Room Details Opened', {
                                                cinemaRoomId: cinemaRoom.id,
                                                cinemaRoomName: cinemaRoom.name,
                                                page: pageName
                                            });
                                            selectCinemaRoom(cinemaRoom);
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
}
